// Mirrors `Messaging::Limits` (uwill-api) - kept here, and only here, so no
// component hardcodes its own copy of these numbers. Still a hand-kept mirror
// of the Ruby source of truth, not fetched from it - if `Messaging::Limits`
// changes, this needs a matching edit.
export const MAX_FILES = 5;
export const FILE_MAX_BYTES = 10 * 1024 * 1024;
export const TOTAL_MAX_BYTES = 25 * 1024 * 1024;
export const ALLOWED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "pdf",
  "docx",
  "xlsx",
  "heic",
  "heif"
];
export const ACCEPT_ATTR = ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(",");

const FAILURE_COPY = {
  attachment_type_rejected: {
    retryable: false,
    beforeQueue: filename =>
      `${filename} wasn't added. You can attach .jpg, .png, .heic, .pdf, .docx, and .xlsx files.`,
    afterQueue: filename =>
      `${filename} didn't upload. This file type isn't supported.`
  },
  file_too_large: {
    retryable: false,
    beforeQueue: filename =>
      `${filename} wasn't added. Files need to be under ${FILE_MAX_BYTES /
        (1024 * 1024)}MB.`,
    afterQueue: filename =>
      `${filename} didn't upload. It's over the ${FILE_MAX_BYTES /
        (1024 * 1024)}MB limit.`
  },
  attachments_too_large: {
    retryable: false,
    afterQueue: filename =>
      `${filename} didn't upload. Your attachments can total up to ${TOTAL_MAX_BYTES /
        (1024 * 1024)}MB.`
  },
  too_many_attachments: {
    retryable: false,
    afterQueue: filename =>
      `${filename} didn't upload. You can attach up to ${MAX_FILES} files at a time.`
  },
  // Request faults, not properties of the file - can only ever be caught
  // server-side (attachFile's catch), always post-queue.
  missing_compose_token: {
    retryable: true,
    afterQueue: () => "Something went wrong. Try again."
  },
  missing_attachment_params: {
    retryable: true,
    afterQueue: () => "Something went wrong. Try again."
  }
};
const REQUEST_FAULT = {
  retryable: true,
  copy: filename =>
    `${filename} didn't upload. Check your connection and try again.`
};

// everQueued: true for anything reached via attachFile's catch (the row
// was already queued/uploading when it failed); false for anything
// rejected before a row was ever created (classifyFile, selection time).
export function classifyFailure(code, everQueued) {
  const entry = FAILURE_COPY[code];
  if (!entry) return REQUEST_FAULT;

  const copy = everQueued ? entry.afterQueue : entry.beforeQueue;
  return { retryable: entry.retryable, copy: copy || entry.afterQueue };
}

const BATCH_FAILURE_COPY = {
  too_many_attachments: () => `You can attach up to ${MAX_FILES} files.`,
  attachments_too_large: () =>
    `These files come to more than ${TOTAL_MAX_BYTES / (1024 * 1024)}MB.`
};

export function classifyBatchFailure(code) {
  return { retryable: false, copy: BATCH_FAILURE_COPY[code] };
}

export function classifyBatch(existingCount, existingBytes, files) {
  if (existingCount + files.length > MAX_FILES) return "too_many_attachments";

  const newBytes = files.reduce((sum, f) => sum + f.size, 0);
  if (existingBytes + newBytes > TOTAL_MAX_BYTES)
    return "attachments_too_large";

  return null;
}

export function classifyFile(file) {
  if (file.size > FILE_MAX_BYTES) return "file_too_large";
  const extension = file.name
    .split(".")
    .pop()
    ?.toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(extension))
    return "attachment_type_rejected";
  return null;
}

// --- whole-send failures (Messaging::SendMessage's attachment codes) -----
//
// A different surface entirely: these fire when the *set* of
// already-uploaded attachment ids is re-validated at send time (another tab
// discarded the draft, the reaper claimed an orphaned row, etc.), not when
// a single file is uploading. Placeholder copy pending a full UX pass -
// only meant to stop a raw error code from reaching the toast.
const SEND_FAILURE_COPY = {
  duplicate_attachment:
    "Something went wrong with your attachments. Try sending again.",
  attachment_not_found:
    "One of your attachments is no longer available. Remove it and try again.",
  attachment_not_ready:
    "One of your attachments hasn't finished uploading yet. Wait a moment and try again.",
  attachment_state_changed:
    "Your attachments changed since you last checked. Review them and try again."
};

export function sendFailureCopy(code) {
  return SEND_FAILURE_COPY[code] || null;
}
